import { useAtomValue } from 'jotai'
import React from 'react'

import { soundClipsAtom } from '../../atoms'
import type { SwitchboardTranslateFn } from '../../types'
import { text } from '../../utils/text'
import { NodeSelect } from './NodeSelect'

export interface VoicemailMessageSelectorProps {
  /** Current selected sound clip ID */
  value?: string
  /** Callback when message selection changes */
  onChange: (
    soundClipId: string | undefined,
    soundClipUuid: string | undefined,
  ) => void
  /** Label for the floating label */
  label?: string
  /** Optional translation function for i18n */
  t?: SwitchboardTranslateFn
  // Note: soundClips is provided via Jotai atoms (soundClipsAtom)
  // Use useSwitchboardDataAtoms hook in the consuming app to set atoms
}

/**
 * Voicemail message selector with special options, sound clips, and legacy options.
 *
 * Data Source: Sound clips from soundClipsAtom (Jotai atom) + static options (assembled client-side)
 * Sound Clips: Fetched via GET /app/v1/sound-clips/?pbx_id={id}
 * Static Options: Hardcoded special options (defined_by_extension, system_generated, no_greeting)
 * Legacy Options: Hardcoded legacy options (busy-1 through custom-9)
 * Documentation: libraries/switchboard-ui/NODE_DATA_POPULATION/leave-voicemail.md
 * Component: LeaveVoicemailNode uses VoicemailMessageSelector
 */
export const VoicemailMessageSelector: React.FC<
  VoicemailMessageSelectorProps
> = ({ value = '', onChange, label = 'Message', t }) => {
  const soundClips = useAtomValue(soundClipsAtom)
  const handleChange = React.useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const soundClipId = e.target.value || undefined
      const soundClipUuid = soundClips.find(
        (clip) => clip.id === soundClipId,
      )?.uuid
      onChange(soundClipId, soundClipUuid)
    },
    [onChange, soundClips],
  )

  // Special voicemail message options (in legacy order) - names translated when t provided
  const specialOptions = [
    {
      id: 'defined_by_extension',
      name: text(
        t,
        'switchboard.core.voicemailOptions.definedByExtension',
        'Defined by extension',
      ),
    },
    {
      id: 'system_generated',
      name: text(
        t,
        'switchboard.core.voicemailOptions.systemGenerated',
        'System Generated Greeting',
      ),
    },
    {
      id: 'no_greeting',
      name: text(
        t,
        'switchboard.core.voicemailOptions.noGreeting',
        'No Greeting',
      ),
    },
  ]

  // Legacy voicemail options - names translated when t provided
  const legacyOptions = [
    {
      id: 'busy-1',
      name: text(t, 'switchboard.core.voicemailOptions.busy1', 'Busy-1'),
    },
    {
      id: 'unavailable-2',
      name: text(
        t,
        'switchboard.core.voicemailOptions.unavailable2',
        'Unavailable-2',
      ),
    },
    {
      id: 'custom-3',
      name: text(t, 'switchboard.core.voicemailOptions.custom3', 'Custom-3'),
    },
    {
      id: 'custom-4',
      name: text(t, 'switchboard.core.voicemailOptions.custom4', 'Custom-4'),
    },
    {
      id: 'custom-5',
      name: text(t, 'switchboard.core.voicemailOptions.custom5', 'Custom-5'),
    },
    {
      id: 'custom-6',
      name: text(t, 'switchboard.core.voicemailOptions.custom6', 'Custom-6'),
    },
    {
      id: 'custom-7',
      name: text(t, 'switchboard.core.voicemailOptions.custom7', 'Custom-7'),
    },
    {
      id: 'custom-8',
      name: text(t, 'switchboard.core.voicemailOptions.custom8', 'Custom-8'),
    },
    {
      id: 'custom-9',
      name: text(t, 'switchboard.core.voicemailOptions.custom9', 'Custom-9'),
    },
  ]

  // Use "defined_by_extension" as default if no value is set
  const displayValue = value || 'defined_by_extension'

  return (
    <NodeSelect label={label} value={displayValue} onChange={handleChange}>
      {/* Special options first (in legacy order) */}
      {specialOptions.map((option) => (
        <option key={option.id} value={option.id}>
          {option.name}
        </option>
      ))}

      {/* Sound clips */}
      {soundClips.length > 0 &&
        soundClips.map((clip) => (
          <option key={clip.id} value={clip.id}>
            {clip.name || clip.id}
          </option>
        ))}

      {/* Legacy options */}
      {legacyOptions.map((option) => (
        <option key={option.id} value={option.id}>
          {option.name}
        </option>
      ))}
    </NodeSelect>
  )
}
